/**
 * Redux async actions (src/store/actions/walletActions.js).
 *
 * This module defines Redux Toolkit thunks (createAsyncThunk) that call backend endpoints via the shared axios client.
 * Slices consume the fulfilled payloads in `extraReducers` to update the UI state.
 */

// walletActions.js

import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import axios from "../../helpers/axiosconfig";
// BUG-004/005: getStoredToken no longer needed - auth is via httpOnly cookie
import { t } from "../../utils/i18nTranslate";

const hasPackageBalanceMap = (walletData) => (
  Boolean(walletData?.packageBalances) &&
  typeof walletData.packageBalances === 'object' &&
  !Array.isArray(walletData.packageBalances)
);

/**
 * Async thunk: fetchWallet.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchWallet = createAsyncThunk(
  'wallet/fetchWallet',
  async (userId, { rejectWithValue }) => {
    try {
      // Keep the legacy thunk on the canonical student wallet contract. The
      // backend exposes balance at /api/wallet/balance/:studentId; there is no
      // generic /api/wallet/:id route.
      const response = await axios.get(`wallet/balance/${userId}`);
      return response.data;
    } catch (error) {
      toast.error(t("store.wallet.fetchDataFailed"));
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk: fetchWalletBalance.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchWalletBalance = createAsyncThunk(
  'wallet/fetchWalletBalance',
  async (studentId, { rejectWithValue }) => {
    try {
      // First fetch the wallet balance
      const balanceResponse = await axios.get(`wallet/balance/${studentId}`);
      const walletData = balanceResponse.data;

      // The packages view can truthfully show an empty state only for a confirmed
      // map. Arrays or missing maps would otherwise be normalized to an empty
      // object and displayed as zero available lessons.
      if (!hasPackageBalanceMap(walletData)) {
        throw new Error('Wallet package balances are unavailable');
      }

      // Then fetch package details for each package ID
      const packagePromises = Object.keys(walletData.packageBalances).map(async (packageId) => {
        try {
          const packageResponse = await axios.get(`packages/${packageId}`);
          // Fetch_Single_Package returns the package in a response envelope.
          // Keep the wallet state compatible with its existing consumers, which
          // read fields such as packageDetails.Package_Name directly.
          const packageDetails = packageResponse.data?.package ?? packageResponse.data;
          return {
            packageId,
            packageDetails
          };
        } catch (error) {
          console.error(`Failed to fetch details for package ${packageId}:`, error);
          return {
            packageId,
            packageDetails: null
          };
        }
      });

      const packageResults = await Promise.all(packagePromises);

      // Merge package details into the wallet balance data
      const enhancedPackageBalances = {};
      packageResults.forEach(({ packageId, packageDetails }) => {
        enhancedPackageBalances[packageId] = {
          ...walletData.packageBalances[packageId],
          packageDetails: packageDetails,
          packageName: packageDetails?.Package_Name // Add package name directly
        };
      });

      return {
        ...walletData,
        packageBalances: enhancedPackageBalances
      };
    } catch (error) {
      toast.error(t("store.wallet.fetchBalanceFailed"));
      return rejectWithValue(error.message);
    }
  }
);



/**
 * Async thunk: purchaseHours.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const purchaseHours = createAsyncThunk(
  'wallet/purchaseHours',
  async ({ studentId, packageId, hours, amount, successMessage }, { rejectWithValue }) => {
    try {
      // Validate inputs
      const validatedHours = Number(hours);
      if (isNaN(validatedHours) || validatedHours <= 0) {
        throw new Error(`Invalid hours value: ${hours}`);
      }

      const validatedAmount = Number(amount) || 0;

      const response = await axios.post('wallet/purchase', { 
        studentId, 
        packageId, 
        hours: validatedHours,
        amount: validatedAmount
      });

      if (validatedAmount > 0) {
        toast.success(successMessage || t("store.wallet.purchaseSuccess", { hours: validatedHours, amount: validatedAmount }));
      } else {
        toast.success(successMessage || t("store.wallet.freeHoursAdded", { hours: validatedHours }));
      }

      return response.data;
    } catch (error) {
      console.error('Purchase hours error:', error);
      const errorMessage = error.response?.data?.message || error.message || t("store.wallet.purchaseFailed");
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk: useHours.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const useHours = createAsyncThunk(
  'wallet/useHours',
  async ({ studentId, packageId, hours, beneficiaryStudentId }, { rejectWithValue, getState }) => {
    try {
      // BUG-004/005: Auth token is in httpOnly cookie, sent automatically via withCredentials.
      const response = await axios.post('/wallet/use',
        { studentId, packageId, hours, beneficiaryStudentId }
      );
      return response.data;
    } catch (error) {
      const errorPayload = error?.response?.data;
      const fallbackMessage = errorPayload?.message || error.message || t("store.wallet.useSessionsFailed");
      return rejectWithValue(errorPayload || fallbackMessage);
    }
  }
);

/**
 * Async thunk: fetchWalletTransactions.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchWalletTransactions = createAsyncThunk(
  'wallet/fetchTransactions',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`wallet/transactions/${studentId}`);
      return response.data.transactions;
    } catch (error) {
      // toast.error('Failed to fetch wallet transactions');
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk: bookLessonWithWallet.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const bookLessonWithWallet = createAsyncThunk(
  'wallet/bookLesson',
  async (formData, { dispatch, rejectWithValue }) => {
    try {
      const payload = Array.isArray(formData) ? formData : formData?.bookings || formData;
      const response = await axios.post('v1/bookings/wallet', payload);
      toast.success(t("store.wallet.lessonBookedSuccess"));
      return response.data;
    } catch (error) {
      const errorPayload = error?.response?.data;
      toast.error(errorPayload?.message || t("store.wallet.lessonBookFailed"));
      return rejectWithValue(errorPayload || error.message);
    }
  }
);

/**
 * Async thunk: addHoursToWallet.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const addHoursToWallet = createAsyncThunk(
  'wallet/addHours',
  async ({ studentId, hours }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`wallet/${studentId}/add`, { hours });
      toast.success(t("store.wallet.sessionsAdded", { hours }));
      return response.data;
    } catch (error) {
      toast.error(t("store.wallet.addSessionsFailed"));
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk: deductHoursFromWallet.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const deductHoursFromWallet = createAsyncThunk(
  'wallet/deductHours',
  async ({ studentId, hours }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`wallet/${studentId}/deduct`, { hours });
      toast.success(t("store.wallet.sessionsDeducted", { hours }));
      return response.data;
    } catch (error) {
      toast.error(t("store.wallet.deductSessionsFailed"));
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk: addPendingHoursToWallet.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const addPendingHoursToWallet = createAsyncThunk(
  'wallet/addPendingHours',
  async ({ studentId, hours }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`wallet/${studentId}/add-pending`, { hours });
      return response.data;
    } catch (error) {
      toast.error(t("store.wallet.addPendingSessionsFailed"));
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk: confirmPendingHours.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const confirmPendingHours = createAsyncThunk(
  'wallet/confirmPendingHours',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`wallet/${studentId}/confirm-pending`);
      toast.success(t("store.wallet.pendingSessionsConfirmed"));
      return response.data;
    } catch (error) {
      toast.error(t("store.wallet.confirmPendingFailed"));
      return rejectWithValue(error.message);
    }
  }
);


// Add these to your existing walletActions.js file

/**
 * Async thunk: fetchWalletHistory.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchWalletHistory = createAsyncThunk(
  'wallet/fetchHistory',
  async ({ studentId, startDate, endDate, limit = 10, page = 1, beneficiaryStudentId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(beneficiaryStudentId && { beneficiaryStudentId }),
        limit,
        page
      });

       
       
      const response = await axios.get(`wallet/history/${studentId}?${params}`);
      
      return response.data.data;
    } catch (error) {
      toast.error(t("store.wallet.noHistoryFound"));
      const responseError = error?.response?.data;
      return rejectWithValue({
        status: error?.response?.status,
        code: responseError?.code,
        message: responseError?.message || error?.message || t("store.wallet.noHistoryFound"),
      });
    }
  }
);

/**
 * Async thunk: fetchPackageWalletHistory.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchPackageWalletHistory = createAsyncThunk(
  'wallet/fetchPackageHistory',
  async ({ studentId, packageId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`wallet/history/${studentId}/package/${packageId}`);
      return response.data.data;
    } catch (error) {
      toast.error(t("store.wallet.fetchPackageHistoryFailed"));
      return rejectWithValue(error.message);
    }
  }
);
